import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Drawer, IconButton, List, ListItemButton, ListItemText, Divider } from '@mui/material';
import { Android, Apple, Shield, VerifiedUser, Block, Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';

interface PublicLayoutProps {
  children: React.ReactNode;
  activeNav?: string;
}

const PublicLayout: React.FC<PublicLayoutProps> = ({ children, activeNav }) => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinkSx = (label: string) => ({
    fontSize: '0.875rem',
    fontWeight: activeNav === label ? 600 : 500,
    color: activeNav === label ? '#dc2626' : '#374151',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s',
    '&:hover': { color: '#dc2626' },
  });

  return (
    <Box sx={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif', overflowX: 'clip' }}>
      {/* Navbar */}
      <Box sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        bgcolor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        boxShadow: scrolled ? '0 1px 20px rgba(0,0,0,0.08)' : 'none',
        transition: 'box-shadow 0.3s',
      }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 5 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 72 }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }} onClick={() => navigate('/login')}>
            <Box component="img" src="/logo-hd.png" alt="CruvzMail" sx={{ height: { xs: 48, md: 77 } }} />
            <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', fontStyle: 'italic', fontFamily: 'Georgia, "Times New Roman", serif', fontWeight: 600, ml: { xs: '52px', md: '88px' }, mt: { xs: -1.5, md: -2.5 }, letterSpacing: '0.02em', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale', display: { xs: 'none', sm: 'block' } }}>Where Business Speaks!</Typography>
          </Box>

          {/* Desktop nav links */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3.5 }}>
            <Typography component="a" sx={navLinkSx('For Business')} onClick={() => navigate('/enterprise')}>For Business</Typography>
            <Typography component="a" sx={navLinkSx('Features')} onClick={() => navigate('/features')}>Features</Typography>
            <Typography component="a" sx={navLinkSx('Migration')} onClick={() => navigate('/migration')}>Migration</Typography>
            <Typography component="a" sx={navLinkSx('Plans and Pricing')} onClick={() => navigate('/signup')}>Plans and Pricing</Typography>
            <Typography component="a" sx={navLinkSx('Support')} onClick={() => navigate('/help-center')}>Support</Typography>
          </Box>

          {/* Right side: desktop buttons + mobile hamburger */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button onClick={() => navigate('/login')} sx={{
              px: { xs: 1.5, md: 2.5 }, py: 1, borderRadius: '8px', fontSize: { xs: '0.8rem', md: '0.875rem' }, fontWeight: 600, textTransform: 'none',
              color: '#374151', border: '1.5px solid #e5e7eb',
              '&:hover': { borderColor: '#dc2626', color: '#dc2626', bgcolor: '#fef2f2' },
            }}>Sign In</Button>
            <Button onClick={() => navigate('/signup')} sx={{
              px: { xs: 1.5, md: 2.5 }, py: 1, borderRadius: '8px', fontSize: { xs: '0.8rem', md: '0.875rem' }, fontWeight: 600, textTransform: 'none',
              color: '#fff', bgcolor: '#dc2626', boxShadow: '0 2px 8px rgba(220,38,38,0.25)',
              display: { xs: 'none', sm: 'flex' },
              '&:hover': { bgcolor: '#b91c1c', boxShadow: '0 4px 16px rgba(220,38,38,0.35)', transform: 'translateY(-1px)' },
            }}>Get Started Free</Button>
            {/* Hamburger — mobile only */}
            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{ display: { xs: 'flex', md: 'none' }, color: '#374151', ml: 0.5 }}
              aria-label="Open menu"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{ sx: { width: 280, pt: 1 } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
            <Box component="img" src="/logo-hd.png" alt="CruvzMail" sx={{ height: 40 }} />
            <IconButton onClick={() => setMobileOpen(false)} aria-label="Close menu"><CloseIcon /></IconButton>
          </Box>
          <Divider />
          <List sx={{ px: 1 }}>
            {[
              { label: 'For Business', path: '/enterprise' },
              { label: 'Features', path: '/features' },
              { label: 'Migration', path: '/migration' },
              { label: 'Plans & Pricing', path: '/signup' },
              { label: 'Support', path: '/help-center' },
            ].map((item) => (
              <ListItemButton
                key={item.label}
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                sx={{ borderRadius: 2, mb: 0.5, '&:hover': { bgcolor: '#fef2f2' } }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: activeNav === item.label ? 700 : 500, color: activeNav === item.label ? '#dc2626' : '#374151', fontSize: '0.95rem' }}
                />
              </ListItemButton>
            ))}
          </List>
          <Divider sx={{ mx: 2 }} />
          <Box sx={{ px: 2, pt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button fullWidth onClick={() => { navigate('/login'); setMobileOpen(false); }} sx={{
              py: 1.25, borderRadius: '8px', fontWeight: 600, textTransform: 'none',
              color: '#374151', border: '1.5px solid #e5e7eb',
              '&:hover': { borderColor: '#dc2626', color: '#dc2626', bgcolor: '#fef2f2' },
            }}>Sign In</Button>
            <Button fullWidth onClick={() => { navigate('/signup'); setMobileOpen(false); }} sx={{
              py: 1.25, borderRadius: '8px', fontWeight: 600, textTransform: 'none',
              color: '#fff', bgcolor: '#dc2626',
              '&:hover': { bgcolor: '#b91c1c' },
            }}>Get Started Free</Button>
          </Box>
        </Drawer>
      </Box>

      {/* Content */}
      {children}

      {/* Footer */}
      <Box sx={{ bgcolor: '#0a0f1a', position: 'relative', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(220,38,38,0.3), rgba(59,130,246,0.3), transparent)' },
      }}>
        {/* Top CTA strip */}
        <Box sx={{ py: { xs: 5, md: 6 }, px: { xs: 2, md: 5 }, background: 'linear-gradient(135deg, rgba(220,38,38,0.08) 0%, rgba(15,23,42,0) 50%, rgba(59,130,246,0.06) 100%)' }}>
          <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
            <Box>
              <Typography sx={{ fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 800, color: '#fff', mb: 0.5, letterSpacing: '-0.02em' }}>Ready to upgrade your business email?</Typography>
              <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)' }}>Join thousands of businesses already using CruvzMail. Free plan available.</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
              <Button onClick={() => navigate('/signup')} sx={{
                px: 3.5, py: 1.5, borderRadius: '12px', fontSize: '0.9rem', fontWeight: 700, textTransform: 'none',
                color: '#fff', background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                boxShadow: '0 4px 20px rgba(220,38,38,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #ef4444, #dc2626)', transform: 'translateY(-1px)', boxShadow: '0 6px 24px rgba(220,38,38,0.4)' },
                transition: 'all 0.3s',
              }}>Start Free Trial</Button>
              <Button onClick={() => navigate('/contact')} sx={{
                px: 3.5, py: 1.5, borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600, textTransform: 'none',
                color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.15)',
                '&:hover': { borderColor: 'rgba(255,255,255,0.35)', color: '#fff', bgcolor: 'rgba(255,255,255,0.05)' },
                transition: 'all 0.3s',
              }}>Contact Sales</Button>
            </Box>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 5 } }}>
          <Box sx={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' }} />
        </Box>

        {/* Main footer content */}
        <Box sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, md: 5 } }}>
          <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: { xs: 6, lg: 10 } }}>
            {/* Brand column */}
            <Box sx={{ flex: '0 0 320px', maxWidth: { xs: '100%', lg: 320 } }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                <Box component="img" src="/Cruvzmail_Footer_logo.png" alt="CruvzMail" sx={{ height: { xs: 60, md: 80 }, alignSelf: 'flex-start' }} />
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', fontStyle: 'italic', fontFamily: 'Georgia, "Times New Roman", serif', fontWeight: 600, ml: { xs: '66px', md: '88px' }, mt: -2, letterSpacing: '0.02em' }}>Where Business Speaks!</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, mb: 3 }}>
                Professional email hosting built for modern businesses. Self-hosted, secure, and fully customizable with your brand.
              </Typography>
              {/* App download badges */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                <Button href="/CruvzMail-v1.0.apk" download sx={{
                  px: 2.5, py: 1.25, borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'none',
                  color: '#fff', bgcolor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.14)', borderColor: 'rgba(255,255,255,0.25)' },
                  transition: 'all 0.25s', display: 'flex', alignItems: 'center', gap: 1,
                }} startIcon={<Android sx={{ fontSize: 20 }} />}>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1 }}>Download on</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>Android</Typography>
                  </Box>
                </Button>
                <Box sx={{
                  px: 2.5, py: 1.25, borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 1,
                  bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', opacity: 0.5,
                }}>
                  <Apple sx={{ fontSize: 20, color: 'rgba(255,255,255,0.4)' }} />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)', lineHeight: 1 }}>Coming soon</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', lineHeight: 1.2 }}>iOS</Typography>
                  </Box>
                </Box>
              </Box>
              {/* Trust badges */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {[
                  { icon: <Shield sx={{ fontSize: 14 }} />, text: '256-bit encryption' },
                  { icon: <VerifiedUser sx={{ fontSize: 14 }} />, text: '99.9% uptime' },
                  { icon: <Block sx={{ fontSize: 14 }} />, text: 'Zero ads' },
                ].map((badge) => (
                  <Box key={badge.text} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '& svg': { color: 'rgba(255,255,255,0.25)' } }}>
                    {badge.icon}
                    <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', fontWeight: 500 }}>{badge.text}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Link columns */}
            <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: { xs: 4, md: 3 } }}>
              {[
                { title: 'Product', links: [
                  { label: 'Features',        href: '/features' },
                  { label: 'Plans & Pricing', href: '/signup' },
                  { label: 'Enterprise',      href: '/enterprise' },
                  { label: 'Migration',       href: '/migration' },
                  { label: 'Security',        href: '/security' },
                  { label: 'Mobile App',      href: '/CruvzMail-v1.0.apk' },
                ]},
                { title: 'Solutions', links: [
                  { label: 'Small Business', href: '/small-business' },
                  { label: 'Enterprise',     href: '/enterprise' },
                  { label: 'Startups',       href: '/startups' },
                  { label: 'Healthcare',     href: '/healthcare' },
                  { label: 'Education',      href: '/education' },
                  { label: 'Non-Profit',     href: '/nonprofit' },
                ]},
                { title: 'Company', links: [
                  { label: 'About Us', href: '/about' },
                  { label: 'Blog',     href: '/blog' },
                  { label: 'Careers',  href: '/careers' },
                  { label: 'Contact',  href: '/contact' },
                ]},
                { title: 'Resources', links: [
                  { label: 'Help Center',   href: '/help-center' },
                  { label: 'Documentation', href: '/documentation' },
                  { label: 'System Status', href: '/status' },
                  { label: 'Community',     href: '/community' },
                ]},
              ].map((col) => (
                <Box key={col.title}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 2.5 }}>{col.title}</Typography>
                  {col.links.map((link) => (
                    <Typography
                      key={link.label}
                      component="a"
                      href={link.href}
                      onClick={(e: React.MouseEvent) => { if (!link.href.endsWith('.apk')) { e.preventDefault(); navigate(link.href); } }}
                      sx={{ display: 'block', fontSize: '0.82rem', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', mb: 1.5, transition: 'all 0.2s', '&:hover': { color: '#fca5a5', transform: 'translateX(2px)' } }}
                    >
                      {link.label}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 5 } }}>
          <Box sx={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' }} />
        </Box>

        {/* Bottom bar */}
        <Box sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 5 } }}>
          <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
              <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>© {new Date().getFullYear()} CruvzMail by Cruvz Online Shopping India Pvt Ltd. All rights reserved.</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
              {[
                { label: 'Privacy Policy', href: '/privacy-policy' },
                { label: 'Terms of Service', href: '/terms-of-service' },
                { label: 'Cookie Policy', href: '/cookie-policy' },
                { label: 'Acceptable Use', href: '/acceptable-use-policy' },
                { label: 'DPA', href: '/data-processing-agreement' },
                { label: 'Refund Policy', href: '/refund-policy' },
                { label: 'SLA', href: '/sla' },
                { label: 'Sitemap', href: '/features' },
              ].map((l, i) => (
                <React.Fragment key={l.label}>
                  {i > 0 && <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.15)' }} />}
                  <Typography component="a" href={l.href} onClick={(e: React.MouseEvent) => { e.preventDefault(); navigate(l.href); }} sx={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.3)', textDecoration: 'none', transition: 'color 0.2s', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}>{l.label}</Typography>
                </React.Fragment>
              ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e', boxShadow: '0 0 8px rgba(34,197,94,0.5)', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>All systems operational</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PublicLayout;
